/**
 * Canonical document content vocabulary (decision 0025).
 *
 * These names describe authored meaning, never presentation. In particular a
 * heading level is semantic outline depth; it is not a font-size instruction.
 */
export const DOCUMENT_BLOCK_KINDS = [
  'paragraph', 'heading', 'quote', 'list', 'list-item', 'embed', 'media',
] as const;

export type DocumentBlockKind = (typeof DOCUMENT_BLOCK_KINDS)[number];

/**
 * THE VOCABULARY EACH VERSION ADMITS — decision `0075`, task `0200`.
 *
 * A document's VERSION names the whole contract a reader must satisfy to read,
 * preserve, edit and write it back, and it moves when an older reader could not
 * do all four. `quote` was added to the closed set in `b5f527a` with every
 * suite green and no version moved, which is the incident this table exists to
 * make impossible to repeat.
 *
 * The v1 list is DERIVED, not remembered: `git show b5f527a^` on this file
 * gives exactly these six, and `b5f527a` gives them plus `quote`.
 *
 * Asserted whole by a clause in this package, so adding a kind to
 * `DOCUMENT_BLOCK_KINDS` without extending a version's list reddens. @Codex's
 * P1 on the reconstruction: a control that knows only about `quote` pins the
 * incident and lets the next `code` or `callout` through in silence.
 */
export const DOCUMENT_VOCABULARY = {
  1: ['paragraph', 'heading', 'list', 'list-item', 'embed', 'media'],
  2: ['paragraph', 'heading', 'quote', 'list', 'list-item', 'embed', 'media'],
} as const satisfies Record<number, readonly DocumentBlockKind[]>;

export type DocumentVersion = keyof typeof DOCUMENT_VOCABULARY;

/** What a writer stamps on content it creates or migrates. */
export const CURRENT_DOCUMENT_VERSION: DocumentVersion = 2;

const VERSIONS = Object.keys(DOCUMENT_VOCABULARY).map(Number) as readonly DocumentVersion[];

/** Is this a version this reader knows the whole contract for? */
export function readableDocumentVersion(version: unknown): version is DocumentVersion {
  return typeof version === 'number' && (VERSIONS as readonly number[]).includes(version);
}

/**
 * WHAT A WRITER STAMPS IS `CURRENT_DOCUMENT_VERSION`, AND ONLY THAT.
 *
 * `requiredDocumentVersion` stood here — *"the lowest version that admits every
 * kind present"* — and it is REMOVED rather than left unused, because the
 * concept is the defect and an exported function is an invitation to reuse it.
 *
 * Decision `0075` rules on this directly, in the section reviewing the
 * reconstruction I wrote, and the sentence is @Codex's:
 *
 *   *"It would also downgrade a v2 document after its last quote is removed,
 *    making the envelope a per-kind feature flag rather than the
 *    writer/reader contract the decision defines. Reserve v1 for unchanged
 *    legacy reads and stamp all successful current writes as v2."*
 *
 * The reconstruction read *"the lowest version that can hold it"* as economy.
 * It is a feature flag, and a reader cannot use a feature flag to decide
 * whether it may write the document back — which is the one job the envelope
 * has.
 *
 * That this shipped is the `0031` trap in its purest form: a lost decision is
 * SILENTLY OBEYED. I reconstructed `0075`, @Codex reviewed the reconstruction
 * and replaced its control 2 in the file itself, and `0200` was then
 * implemented against my own superseded control rather than against the
 * ruling sitting in the same document. A decision marked accepted is binding;
 * drifting from it in code is the thing `CLAUDE.md` names.
 *
 * Version 1 is now what an unchanged legacy document is READ at, and nothing
 * else. `DOCUMENT_VOCABULARY` keeps its job — it says which kinds a given
 * version's reader understands, which is what grandfathering asks — and no
 * longer decides what a write stamps.
 */

export interface DocumentBlock {
  /** Stable object identity. Carets and relations anchor here, not in the DOM. */
  readonly id: string;
  readonly kind: DocumentBlockKind;
  /** Text authored into textual blocks. Absent on embeds and containers. */
  readonly text?: string;
  /** Semantic outline depth, valid only for headings. */
  readonly level?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Another canonical Morph object, valid only for embeds and media. */
  readonly target?: string;
  /** Ordered canonical children, valid only for lists. */
  readonly children?: readonly string[];
}

export interface DocumentContent {
  readonly version: DocumentVersion;
  /** Ordered roots; order belongs to content, not to a layout spec. */
  readonly roots: readonly string[];
  readonly blocks: readonly DocumentBlock[];
  /**
   * Exact delimiters retained only while a legacy Body slot must round-trip.
   * This is migration-envelope metadata, not authored block structure. Remove
   * it with the compatibility writer rather than teaching editors to mutate it.
   */
  readonly legacySeparators?: readonly string[];
}

export interface DocumentProblem {
  readonly path: string;
  readonly message: string;
}

const KINDS = new Set<string>(DOCUMENT_BLOCK_KINDS);

/** Validate the semantic graph at its serialization boundary. */
export function validateDocumentContent(value: DocumentContent): readonly DocumentProblem[] {
  const problems: DocumentProblem[] = [];
  /* THE VERSION GATE COMES FIRST, AND ALONE — decision `0075` ruling 5, and
     @Codex's P1 on the rollout: a reader that walks the blocks of a version it
     cannot read reports a graph or block error where it owes a refusal about
     the FORMAT. So an unreadable version returns immediately with one problem
     naming the version, having touched nothing else.

     A future version is not malformed content; it is content this build does
     not know the contract for, and the difference is what lets a person be
     told to update rather than told their document is broken. */
  if (!readableDocumentVersion(value.version)) {
    return [{ path: 'version', message: `is version ${String(value.version)}, which this build cannot read` }];
  }
  // A DOCUMENT WITH NO BLOCKS HAS NOWHERE TO PUT THE CARET. `emptyDocumentContent`
  // establishes one persisted empty paragraph as the canonical empty state, and
  // this boundary accepted `{ roots: [], blocks: [] }` anyway — so cleanup, an
  // import, or an editor deleting the last block could serialize and persist the
  // exact unusable state that rule exists to prevent. The cleanup rule below
  // already forbids removing the only paragraph; this is the same rule stated
  // where it can actually be enforced.
  if (value.roots.length === 0) problems.push({ path: 'roots', message: 'a document has at least one block' });
  const byId = new Map<string, DocumentBlock>();
  value.blocks.forEach((block, index) => {
    const path = `blocks[${index}]`;
    if (!block.id) problems.push({ path: `${path}.id`, message: 'must not be empty' });
    if (byId.has(block.id)) problems.push({ path: `${path}.id`, message: 'must be unique' });
    byId.set(block.id, block);
    /* AGAINST THE UNION OF EVERY VERSION, NOT AGAINST THIS ENVELOPE'S OWN —
       @Codex's second P1. This product has ALREADY persisted seeded quotes
       under version 1, so a v1 envelope carrying a v2 kind is GRANDFATHERED:
       read now, and upgraded to the current version on the first genuine edit,
       exactly as ruling 6 says for any legacy document. Malformed is reserved
       for a kind that is in NO version's vocabulary. Classifying v1-plus-quote
       as malformed would strand those documents before a lazy migration could
       ever run — unreachable rather than upgradeable. */
    if (!KINDS.has(block.kind)) problems.push({ path: `${path}.kind`, message: 'is not in the closed vocabulary' });
    if (block.kind === 'heading' && (block.level === undefined || block.level < 1 || block.level > 6))
      problems.push({ path: `${path}.level`, message: 'headings require a level from 1 through 6' });
    if (block.kind !== 'heading' && block.level !== undefined)
      problems.push({ path: `${path}.level`, message: 'is only valid on headings' });
    if ((block.kind === 'embed' || block.kind === 'media') !== (block.target !== undefined))
      problems.push({ path: `${path}.target`, message: 'is required only on embed and media blocks' });
    if ((block.kind === 'list') !== (block.children !== undefined))
      problems.push({ path: `${path}.children`, message: 'is required only on list blocks' });
  });
  if (value.legacySeparators !== undefined) {
    if (value.legacySeparators.length !== Math.max(0, value.roots.length - 1))
      problems.push({ path: 'legacySeparators', message: 'must have one separator between each root' });
    value.legacySeparators.forEach((separator, index) => {
      if (!/^(?:\r?\n){2}$/.test(separator))
        problems.push({ path: `legacySeparators[${index}]`, message: 'must be an exact legacy blank-line delimiter' });
    });
  }
  const referenced = new Set<string>();
  const requireBlock = (id: string, path: string) => {
    if (!byId.has(id)) problems.push({ path, message: `references missing block ${id}` });
    if (referenced.has(id)) problems.push({ path, message: `block ${id} has more than one structural parent` });
    referenced.add(id);
  };
  value.roots.forEach((id, index) => requireBlock(id, `roots[${index}]`));
  value.blocks.forEach((block, index) => block.children?.forEach((id, child) => {
    requireBlock(id, `blocks[${index}].children[${child}]`);
    if (byId.get(id)?.kind !== 'list-item')
      problems.push({ path: `blocks[${index}].children[${child}]`, message: 'list children must be list-item blocks' });
  }));
  value.blocks.forEach((block, index) => {
    if (!referenced.has(block.id)) problems.push({ path: `blocks[${index}].id`, message: 'block is unreachable' });
  });
  return problems;
}

/**
 * A writable empty document has an intentional canonical caret target.
 *
 * @Codex, answering the empty-document question decision 0025 raised: the empty
 * state is **one empty paragraph object**, persisted, not an ephemeral DOM-only
 * paragraph. Decision 0024 made this the first document the canvas will meet —
 * creation now stores no title and no body at all, so "nothing" is the ordinary
 * case rather than the edge one.
 *
 * Its cleanup rule, recorded here because the object is meaningless without it:
 * redundant empty paragraphs may be removed, but never the document's only
 * paragraph, never the active caret's block, and never an empty trailing
 * paragraph currently serving as the insertion position.
 */
export function emptyDocumentContent(blockId: string): DocumentContent {
  return {
    version: CURRENT_DOCUMENT_VERSION,
    roots: [blockId],
    blocks: [{ id: blockId, kind: 'paragraph', text: '' }],
  };
}

/** Lossless, deterministic interchange form. Canonical storage remains objects. */
export function serializeDocumentContent(content: DocumentContent): string {
  const problems = validateDocumentContent(content);
  if (problems.length) throw new Error(`invalid document content: ${problems[0]!.path} ${problems[0]!.message}`);
  return JSON.stringify(content);
}

export function parseDocumentContent(serialized: string): DocumentContent {
  const value = JSON.parse(serialized) as DocumentContent;
  const problems = validateDocumentContent(value);
  if (problems.length) throw new Error(`invalid document content: ${problems[0]!.path} ${problems[0]!.message}`);
  return value;
}
